import { graphql } from 'gatsby';
import React, { FC } from 'react';

import {
  Card,
  CardBody,
  CardOptions,
  CardOptionsButton,
  ControlledModal,
  InlineIconButton,
} from '@/components';
import { useDataGrid } from '@/components/DataGrid';
import {
  EyeIcon,
  NextIcon,
  PreviousIcon,
  RefreshIcon,
} from '@/components/icons';
import { useTranslate } from '@/contexts';
import { useBrandValueOptions } from '@/hooks/useBrands';
import RewardTemplates from './RewardTemplates';
import useWelcomeOffers from './useWelcomeOffers';

export const Fragment = graphql`
  fragment SanityWelcomeOffersBlock on SanityWelcomeOffersBlock {
    title {
      ...SanityLocaleString
    }
  }
`;

const WelcomeOffersBlock: FC<{
  block: Queries.SanityWelcomeOffersBlock;
}> = ({ block }) => {
  const { t } = useTranslate();

  const {
    welcomeOffers,
    brands,
    fetching,
    nextPage,
    previousPage,
    refresh,
    orderBy,
    desc,
    setOrderBy,
    defaultFilter,
    updateFilter,
  } = useWelcomeOffers();

  const brandValueOptions = useBrandValueOptions(brands);

  const { DataGrid, GlobalCardOptions } = useDataGrid({
    name: 'welcomeOffers',
    data: welcomeOffers,
    fetching,
    orderBy,
    setOrderBy,
    desc,
    defaultFilter,
    updateFilter,
    schema: (s) => [
      s.stringValue({
        field: 'name',
        title: 'Name',
      }),
      s.stringValue({
        field: 'id',
        title: 'Offer ID',
      }),
      s.selectValue({
        field: 'brand',
        title: 'Brand',
        filterField: 'brand',
        options: brandValueOptions.options,
        getValue: ({ row }) => row.brand.name,
      }),
      s.stringValue({
        field: 'countries',
        title: 'Countries',
        filterField: 'country',
      }),
      s.stringValue({
        field: 'currency',
        title: 'Currency',
        filterField: 'currency',
      }),
      s.stringValue({
        field: 'affiliateId',
        title: 'Affiliate ID',
        filterField: 'affiliateId',
      }),
      s.stringValue({
        field: 'depositNumber',
        title: 'Deposit Number',
        filterField: 'depositNumber',
      }),
      s.dateTimeValue({
        field: 'expirationDate',
        title: 'Expiration Date',
      }),
      s.stringValue({
        field: 'modifiedBy',
        title: 'Modified By',
      }),
      s.dateTimeValue({
        field: 'modifiedAt',
        title: 'Modified At',
      }),
      s.stringValue({
        field: 'version',
        title: 'Version',
      }),
      s.actionValue({
        field: 'rewardTemplates',
        title: 'Reward Templates',
        getValue: ({ row }) => (
          <ControlledModal
            content={
              <RewardTemplates
                name={row.name}
                templates={row.rewardTemplates || []}
              />
            }
          >
            <InlineIconButton>
              {row.rewardTemplates && row.rewardTemplates.length > 0 ? (
                <div className="flex items-center gap-1">
                  <EyeIcon />
                  {row.rewardTemplates.length}
                </div>
              ) : (
                'No templates'
              )}
            </InlineIconButton>
          </ControlledModal>
        ),
      }),
    ],
  });

  return (
    <Card
      size="lg"
      title={t(block.title)}
      showOptionsAtBottom
      options={
        <CardOptions>
          <CardOptionsButton
            disabled={!previousPage}
            onClick={() => previousPage && previousPage()}
          >
            <PreviousIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={!nextPage}
            onClick={() => nextPage && nextPage()}
          >
            <NextIcon />
          </CardOptionsButton>
          <GlobalCardOptions />
          <CardOptionsButton
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <DataGrid />
      </CardBody>
    </Card>
  );
};

export default WelcomeOffersBlock;
